import * as React from 'react';
import { useEffect } from "react";
import { Chart } from "chart.js";
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData } from "../chart-types";
import {
	chartConfig,
	axesConfig,
	setTooltipColorScheme,
	horizontalBarValue,
	numberFormat,
    barChartConfigPlugin,
} from "../chart-utils";
import { ChartContainer } from "./chart-container";
import { chartBarDataPointPatterns } from "../chart-patterns";
import { getText } from "../../../translations";

const BarHorizontalChart = ({
	title,
	data,
	siteVariables,
	stacked,
	customColors,
	layout,
	mirror,
	yLabelOffset,
	customHeight,
	barPercentage,
	categoryPercentage,
	fillBarLength,
	ticksMax,
	backgroundBar,
	textBar,
	gradientBar,
	hideLegend,
	barPercent,
	customLegend,
	customTooltip,
	activeSegmentIndex,
	onChangeSegmentIndex,
	theme,
}: {
	title: string;
	data: IChartData;
	siteVariables: SiteVariablesPrepared;
	stacked?: boolean;
	customColors?: string[];
	layout?: any;
	mirror?: boolean;
	yLabelOffset?: number;
	customHeight?: string;
	barPercentage?: number;
	categoryPercentage?: number;
	fillBarLength?: boolean;
	ticksMax?: number;
	backgroundBar?: boolean;
	textBar?: boolean;
	gradientBar?: boolean;
	hideLegend?: boolean;
	barPercent?: boolean;
	customLegend?: boolean;
	customTooltip?: boolean;
	activeSegmentIndex?: number[] | number;
	onChangeSegmentIndex?: Function;
	theme?: string;
}) => {
	const { colorScheme, colors, t } = siteVariables;
	const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
	const chartRef = React.useRef<Chart | undefined>();
	const chartId = React.useMemo(
		() => Math.random().toString(36).substr(2, 9),
		[]
	);
	const chartDataPointColors = React.useMemo(
		() => customColors ? customColors : [
			'#27438C',
		],
		[theme]
	);

	const createDataPoints = (): Chart.ChartDataSets[] =>
		Array.from(data.datasets, (set, i) => {
			const totalPorLabel = Array.isArray(data.labels) && data.labels.map((label, idx) => {
				return data.datasets.reduce((sum, dataset) => sum as any + dataset.data[idx] as any, 0);
			});

			const dataPercentual = set.data.map((value, idx) => {
				const totalLabel = totalPorLabel[idx];
				return totalLabel === 0 ? 0 : (value as any / totalLabel) * 100;
			});

			const colorsForBars = set.data.map((_, idx) => {
				return customColors[idx % customColors.length];
			});

			let dataPointConfig = {
				label: getText(t?.locale, set.label),
				data: barPercent ? dataPercentual : set.data,
				borderWidth: 0,
				barPercentage: 0.5,
				borderSkipped: false,
				borderColor: colorScheme.default.background,
				hoverBorderColor: chartDataPointColors[i],
				backgroundColor: customColors && data.datasets.length == 1 ? colorsForBars : chartDataPointColors[i],
				hoverBorderWidth: 0,
				hoverBackgroundColor: customColors && data.datasets.length == 1 ? colorsForBars : chartDataPointColors[i],
				pointBorderColor: colorScheme.default.background,
				pointBackgroundColor: colorScheme.default.foreground3,
				pointHoverBackgroundColor: colorScheme.default.foreground3,
				pointHoverBorderColor: chartDataPointColors[i],
				pointHoverBorderWidth: 0,
				borderCapStyle: "round",
				borderJoinStyle: "round",
				pointBorderWidth: 0,
				pointRadius: 0,
				pointHoverRadius: 0,
			};

			return dataPointConfig as any;
		});

	useEffect(() => {
		let selectedIndex = -1;
		let selectedDataSet = 0;

		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;
		const config: any = barChartConfigPlugin({ type: "horizontalBar", selectedIndex: null });

		config.options.layout.padding.top = layout.top;
		config.options.layout.padding.left = layout.left;
		config.options.layout.padding.right = layout.right;

		config.options.hover.mode = "index";

		config.options.scales.xAxes[0].ticks.display = false;
		config.options.scales.xAxes[0].gridLines.display = false;

		config.options.scales.yAxes[0].ticks.callback = (v: string) => v;
		config.options.scales.yAxes[0].ticks.mirror = mirror;
		config.options.scales.yAxes[0].ticks.padding = 0;
		config.options.scales.yAxes[0].gridLines.display = false;
		config.options.scales.yAxes[0].barPercentage = barPercentage; 
		config.options.scales.yAxes[0].categoryPercentage = categoryPercentage;

		config.options.indexAxis = "y";

		config.options.tooltips.position = "nearest";
		config.options.scales.yAxes[0].ticks.fontSize = 12;

		if (stacked) {
			config.options.hover.mode = "point";

			config.options.scales.yAxes[0].ticks.padding = -30;
			config.options.scales.yAxes[0].position = 'left';

			config.options.scales.yAxes[0].stacked = true;
			config.options.scales.xAxes[0].stacked = true;
			config.options.tooltips.mode = "nearest";
			config.options.tooltips.axis = "y";
			config.options.tooltips.callbacks.title = (tooltipItems: any) => {
				let total = 0;

				data.datasets.map((dataset) => {
					const value = dataset.data[tooltipItems[0].index];

					if (typeof value === "number") {
						return (total += value);
					}
				});

				if (barPercent) {
					const datasetIndex = tooltipItems[0].datasetIndex;
					const dataset = data.datasets[datasetIndex];
					const originalValue = dataset.data[tooltipItems[0].index];

					const percentage = total > 0 ? ((originalValue as number / total) * 100).toFixed(2) : 0;

					return `${originalValue} (${percentage}%)`;
				}

				return tooltipItems[0].xLabel;
			};

			config.options.tooltips.callbacks.footer = (tooltipItems: any) => {  return "" }
		}

		if (yLabelOffset) {
			config.options.scales.yAxes[0].ticks.labelOffset = yLabelOffset;
			config.options.scales.yAxes[0].ticks.padding = 0;
		}

		if (fillBarLength) {
			config.options.scales.xAxes[0].ticks.max = ticksMax;
			config.options.scales.xAxes[0].ticks.min = 0;
			config.options.scales.xAxes[0].ticks.stepSize = Math.floor(ticksMax / 5);

			config.options.scales.yAxes[0].ticks.padding = yLabelOffset ? 0 : -30;
			config.options.scales.yAxes[0].position = 'left';
		}

		config.options.onHover = (event, chartElement) => {
			event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
		}

		config.options.onClick = (e, items) => {
			if (items.length === 0) return;
			const clickedIndex = items[0]._index;
			const clickedDatasetIndex = items[0]._datasetIndex;

			if (items[0]._chart.config.data.datasets.length > 1) {
				if (activeSegmentIndex && (activeSegmentIndex[0] == clickedIndex && activeSegmentIndex[1] == clickedDatasetIndex)) {
					onChangeSegmentIndex(null, null);
				} else {
					onChangeSegmentIndex(clickedIndex, clickedDatasetIndex);
				}
			} else {
				if (activeSegmentIndex == clickedIndex) {
					onChangeSegmentIndex(null);
				} else {
					onChangeSegmentIndex(clickedIndex);
				}
			}
		}

		chartRef.current = new Chart(ctx, {
			...(config as any),
			data: {
				labels: Array.isArray(data.labels)
					? data.labels.map((label) => getText(t?.locale, label))
					: getText(t?.locale, data.labels),
				datasets: [],
			},
			legend: {
				display: hideLegend ? hideLegend : false,
			},
			plugins: [
				{
					afterDatasetsDraw: ({ ctx, tooltip, chart }: any) => {
						if (!fillBarLength)
						horizontalBarValue({
							chart,
							ctx,
							stacked,
							activeSegmentIndex,
						});

						if (fillBarLength) {
							var ctx = chart.chart.ctx,
								width = chart.chartArea.right;
							chart.data.datasets.forEach(function (dataset, datasetIndex) {
								var datasetMeta = chart.getDatasetMeta(datasetIndex);
								datasetMeta.data.forEach(function (segment, segmentIndex) {
									if (backgroundBar) {
										var height = segment._model.height,
											posX = segment.tooltipPosition().x,
											posY = segment.tooltipPosition().y - (height / 2);

										ctx.save();
										ctx.fillStyle = theme === "dark" ? '#525252' : '#F5F5F5';
										ctx.fillRect(posX, posY, width - posX, height);
										ctx.restore();
									}

									if (textBar) {
										var value = dataset.data[segmentIndex];
										var x = segment._model.base + (segment._xScale.width - 10);

										ctx.font = "bold 11px Segoe UI";
										ctx.textAlign = "right";
										ctx.textBaseline = "middle";
										

										if (activeSegmentIndex != null) {
											if (segmentIndex == activeSegmentIndex) {
												ctx.fillStyle = theme === "dark" ? "white" : "rgb(37, 36, 35)";
											} else {
												ctx.fillStyle = fillBarLength ? theme === "dark" ? "rgb(59, 58, 57)" : "#e9e9e9" : theme === "dark" ? "#525252" : "#f5f5f5";
											}
										} else {
											ctx.fillStyle = chart.options.defaultColor;
										}

										ctx.fillText(value, x, segment._model.y);

										ctx.restore();
									}
								});
							});
						}
					},
				},
			],
		});

		const chart: any = chartRef.current;

		return () => {
			if (!chartRef.current) return;

			chartRef.current.destroy();
		};
	}, [data, activeSegmentIndex]);

	useEffect(() => {
		if (!chartRef.current) return;
		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;


		if (data.datasets.length > 1) {
			const updatedDatasets = data.datasets.map((dataset, datasetIndex) => {
				const totalPorLabel = Array.isArray(data.labels) && data.labels.map((label, idx) => {
					return data.datasets.reduce((sum, dataset) => sum as any + dataset.data[idx] as any, 0);
				});

				const dataPercentual = dataset.data.map((value, idx) => {
					const totalLabel = totalPorLabel[idx];
					return totalLabel === 0 ? 0 : (value as any / totalLabel) * 100;
				});

				let updatedColors = dataset.data.map((_, dataIndex) => {
					return activeSegmentIndex && (activeSegmentIndex[0] === dataIndex && activeSegmentIndex[1] == datasetIndex) ? customColors[datasetIndex] : theme === "dark" ? "#525252" : "#f5f5f5";
				});

				if (activeSegmentIndex) {
					if (activeSegmentIndex[0] == null && activeSegmentIndex[1] == null) {
						const colorsForBars = dataset.data.map((_, idx) => {
							return customColors[datasetIndex];
						});

						updatedColors = colorsForBars;
					}
				}

				return {
					...dataset,
					data: barPercent ? dataPercentual : dataset.data,
					label: getText(t?.locale, dataset.label),
					backgroundColor: updatedColors,
					hoverBackgroundColor: updatedColors,
				};
			});

			chartRef.current.data.datasets = updatedDatasets;

			if (activeSegmentIndex != null && (activeSegmentIndex[0] != null && activeSegmentIndex[1] != null)) {
				chartRef.current.options.animation.duration = 0;
			}
		} else {
			let updatedColors = data.datasets[0].data.map((_, index) => activeSegmentIndex === index ? customColors[index] : fillBarLength ? theme === "dark" ? "#3d3d3d" : "#e9e9e9" : theme === "dark" ? "#525252" : "#f5f5f5");

			if (activeSegmentIndex == null)
				updatedColors = data.datasets[0].data.map((_, index) => customColors[index]);

			chartRef.current.data.datasets = [
				{
					label: getText(t?.locale, data.datasets[0].label),
					data: data.datasets[0].data,
					backgroundColor: updatedColors,
					hoverBackgroundColor: updatedColors,
				},
			];

			if (activeSegmentIndex != null) {
				chartRef.current.options.animation.duration = 0;
			}
		}
		

		chartRef.current.update();
	}, [activeSegmentIndex]);

	/**
	 * Theme updates
	 */
	useEffect(() => {
		if (!chartRef.current) return;
		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;
		// Apply new colors scheme for data points
		activeSegmentIndex == null ? chartRef.current.data.datasets = createDataPoints() : null;

		if (gradientBar) {
			var gradient = ctx.createLinearGradient(200, 0, 0, 0);
			gradient.addColorStop(0, customColors[0]);
			gradient.addColorStop(1, customColors[1]);

			chartRef.current.data.datasets[0].backgroundColor = gradient;
		}

		// Update tooltip colors scheme
		setTooltipColorScheme({
			chart: chartRef.current,
			siteVariables,
			chartDataPointColors,
			patterns: chartBarDataPointPatterns,
			customTooltip: customTooltip,
		});
		// Update axeses
		axesConfig({ chart: chartRef.current, ctx, colorScheme });
		chartRef.current.options.defaultColor = colorScheme.default.foreground;
		chartRef.current.update();
	}, [theme, data, activeSegmentIndex]);

	function onLegendClick(datasetIndex: number) {
		if (!chartRef.current) return;
		chartRef.current.data.datasets![datasetIndex].hidden = !chartRef.current
			.data.datasets![datasetIndex].hidden;
		chartRef.current.update();
	}

	return (
		<ChartContainer
			siteVariables={siteVariables}
			data={data}
			chartDataPointColors={chartDataPointColors}
			patterns={chartBarDataPointPatterns}
			onLegendClick={onLegendClick}
			hideLegend={hideLegend}
			customHeight={customHeight}
			customLegend={customLegend}
			activeSegmentIndex={activeSegmentIndex}
		>
			<canvas
				id={chartId}
				ref={canvasRef}
				tabIndex={0}
				style={{
					userSelect: "none",
				}}
				aria-label={title}
			>
				{data.datasets.map((set, setKey) =>
					(set.data as number[]).forEach((item: number, itemKey: number) => (
						// Generated tooltips for screen readers
						<div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
							<p>{item}</p>
							<span>
								{getText(t?.locale, set.label)}: {set.data[itemKey]}
							</span>
						</div>
					))
				)}
			</canvas>
		</ChartContainer>
	);
};

export default BarHorizontalChart;